import { useSyncExternalStore } from "react";

/**
 * The connection light, as a tiny external store.
 *
 *   online   — the last request got an answer from the API (any answer: a 409
 *              or a 422 is the server talking, so the line is fine)
 *   degraded — the API was reached but a dependency did not answer (503), or
 *              the answer was not our envelope (a proxy's 502 page)
 *   offline  — the request never reached anything, or the browser says it
 *              has no network
 *
 * The API client reports every outcome via `reportOutcome`; the browser's
 * online/offline events feed in too. Nothing here ever signs anyone out.
 */
export type ConnectionState = "online" | "degraded" | "offline";

let lastOutcome: ConnectionState = "online";
let browserOnline = typeof navigator === "undefined" ? true : navigator.onLine !== false;
const listeners = new Set<() => void>();

function emit(): void {
	for (const fn of listeners) fn();
}

export function getConnection(): ConnectionState {
	if (!browserOnline) return "offline";
	return lastOutcome;
}

/** Called by the API client after every request settles (aborts excluded). */
export function reportOutcome(state: ConnectionState): void {
	if (state === lastOutcome) return;
	lastOutcome = state;
	emit();
}

if (typeof window !== "undefined") {
	window.addEventListener("online", () => {
		browserOnline = true;
		emit();
	});
	window.addEventListener("offline", () => {
		browserOnline = false;
		emit();
	});
}

function subscribe(fn: () => void): () => void {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

/** `"online" | "degraded" | "offline"` — re-renders when it changes. */
export function useConnection(): ConnectionState {
	return useSyncExternalStore(subscribe, getConnection, () => "online");
}
